import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';

export type AnalyticsSourceMiddlewareEventPayload = {
  obj: {
    type: 'track' | 'identify' | 'page' | 'group' | 'alias';
    event?: string;
    messageId: string;
    anonymousId: string;
    userId?: string;
    timestamp: Date;
    integrations?: Record<string, any>;
    context: {
      campaign?: Record<string, any>;
      library?: { name: string; version: string };
      locale?: string;
      timezone?: string;
      userAgent?: string;
      userAgentData?: {
        platform: string;
        mobile: boolean;
        brands: {
          brand: string;
          version: string;
        }[];
      };
      page?: {
        path: string;
        referrer: string;
        search: string;
        title: string;
        url: string;
      };
    };
  };
};

export type AnalyticsSourceMiddlewareEvent = {
  payload: AnalyticsSourceMiddlewareEventPayload;
  defaultPrevented?: boolean;
};

export type AnalyticsSourceMiddlewareCallback = (
  e: AnalyticsSourceMiddlewareEvent,
) => void;

const kParamsKey = 'analytics.session.params';

export const analyticsCampaignSessionParams$ = new BehaviorSubject<
  Record<string, any>
>({});

const kAdsParams = [
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'twclid',
  'clid_src',
  'msclkid',
  'ttclid',
];

export function getAnalyticsSessionAdsParams() {
  return Object.fromEntries(
    Object.entries(analyticsCampaignSessionParams$.value).filter(([key]) =>
      kAdsParams.includes(key),
    ),
  );
}

export function getAnalyticsSessionCampaignParams(options?: {
  stripePrefix?: boolean;
}) {
  if (!analyticsCampaignSessionParams$.value?.utm_campaign) {
    return undefined;
  }
  return Object.fromEntries(
    Object.entries(analyticsCampaignSessionParams$.value)
      .filter(([key]) => key.startsWith('utm_'))
      .map(([key, value]) => [
        options?.stripePrefix ? key.slice(4) : key,
        value,
      ]),
  );
}

export function captureAnalyticsSessionParams() {
  if (Platform.OS !== 'web') return;

  const s = sessionStorage.getItem(kParamsKey);
  if (s) {
    analyticsCampaignSessionParams$.next(JSON.parse(s));
  }

  const url = new URL(location.href);

  const additional_params: Record<string, string> = {};

  if (url.searchParams.has('utm_campaign')) {
    const utm_params = Object.fromEntries(
      [...url.searchParams.entries()]
        .filter(
          ([key, value]) => key.startsWith('utm_') && typeof value === 'string',
        )
        .map(([key, value]) => [key, value as string]),
    );
    if (Object.keys(utm_params).length) {
      analyticsCampaignSessionParams$.next({
        ...analyticsCampaignSessionParams$.value,
        ...utm_params,
      });
    }
  }

  for (const key of kAdsParams) {
    if (
      url.searchParams.has(key) &&
      typeof url.searchParams.get(key) === 'string'
    ) {
      additional_params[key] = url.searchParams.get(key) as string;
    }
  }

  if (Object.keys(additional_params).length) {
    analyticsCampaignSessionParams$.next({
      ...analyticsCampaignSessionParams$.value,
      ...additional_params,
    });
  }

  sessionStorage.setItem(
    kParamsKey,
    JSON.stringify(analyticsCampaignSessionParams$.value),
  );
}
